import React, { FC, KeyboardEvent, useCallback } from 'react';
import styled, { stylesheet } from 'astroturf/react';

import Modal from 'js/components/modals/Modal';
import { Button as ButtonBase } from 'js/design_system/Button';
import { TextButton as TextButtonBase } from 'js/design_system/TextButton';
import { Heading as HeadingBase } from 'js/design_system/Heading';
import { Colors } from 'js/design_system/Color';
import CloseIcon from 'js/design_system/icons/close.svg';

const Button = styled(ButtonBase)`
  font-size: 14px;
  height: 32px;
`;

const TextButton = styled(TextButtonBase)`
  font-size: 14px;
  height: 32px;
`;

const Buttons = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
`;

const CloseButton = styled(TextButtonBase)`
  --size: 48px;
  height: var(--size);
  margin-right: -12px;
  padding: 0;
  width: var(--size);
`;

const Heading = styled(HeadingBase)`
  font-size: 16px;
  line-height: 20px;

  @include for-tablet-up {
    font-size: 22px;
    line-height: 28px;
  }
`;

const HeadingGroup = styled.div<{ border?: boolean }>`
  align-items: center;
  display: flex;
  justify-content: space-between;

  &.border {
    border-bottom: 1px solid $ds-gray-light;
    margin-bottom: 16px;
  }
`;

const Content = styled.div`
  margin-bottom: 16px;
  min-height: 64px;
`;

const styles = stylesheet`
  .backdrop {
    align-items: flex-start;

    @include for-tablet-up {
      align-items: center;
    }
  }

  .modal {
    padding: 8px 16px;
    width: calc(100% - 16px);
    border-radius: 2px;
    margin: 8px;

    @include for-tablet-up {
      padding: 16px 32px 24px;
      width: 60vw;
      margin: 0;
    }
  }
`;

type Props = {
  heading?: string;
  content: React.ReactNode;
  action: string;
  disableAction: boolean;
  /**
   * Clicking outside the modal, clicking Cancel,
   * or clicking the close 'X' calls this function.
   */
  onClose: () => void;
  /**
   * This function is called when a user clicks the primary action
   * button.
   */
  onAction: () => void;
};

const ConfirmationModal: FC<Props> = ({
  heading,
  content,
  action,
  disableAction,
  onClose,
  onAction,
}) => {
  const closeModalOnEscape = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Modal
      backdropClassName={styles.backdrop}
      className={styles.modal}
      onClose={onClose}
      containerProps={{
        onKeyDown: closeModalOnEscape,
      }}
    >
      <HeadingGroup border={!!heading}>
        <Heading size="sm">{heading}</Heading>

        <CloseButton aria-label="Close" onClick={onClose}>
          <CloseIcon color={Colors.GrayDark} />
        </CloseButton>
      </HeadingGroup>

      <Content>{content}</Content>

      <Buttons>
        <ButtonGroup>
          <TextButton inline onClick={onClose}>
            Cancel
          </TextButton>
          <Button disabled={disableAction} onClick={onAction}>
            {action}
          </Button>
        </ButtonGroup>
      </Buttons>
    </Modal>
  );
};

export default ConfirmationModal;
